/**
 * On-demand auto-detection over existing recordings (run lives in the qce slice).
 * Auto-detect on NEW recordings is a separate feature: see ThemeSettingsModal.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import TSPrimaryButton from "../../../../components/common/buttons/TSPrimaryButton";
import TSSecondaryButton from "../../../../components/common/buttons/TSSecondaryButton";
import type { RootState } from "../../../../store/state/rootReducer";
import { useThemes, useQceDispatch, runThemeDetection, clearAutoDetectRun } from "../../../../store/state/qce/qceSlice";

import { ThemeIcon } from "./icons";
import type { AutoDetectRunRequest, AutoDetectRunResult, AutoDetectStrength } from "./types";

const PER_THEME_OPTIONS: AutoDetectRunRequest["perTheme"][] = [3, 5, 10, 20, 50, 100];
const STRENGTH_OPTIONS: { value: AutoDetectStrength; label: string; tooltip: string }[] = [
  { value: "strong", label: "Strong", tooltip: "Fewer, closer matches" },
  { value: "balanced", label: "Balanced", tooltip: "A mix of match count and closeness" },
  { value: "broad", label: "Broad", tooltip: "More, looser matches" },
];
// "none" stands in for null (no limit) — ToggleButtonGroup can't carry null values.
const PER_RECORDING_OPTIONS: { value: "3" | "5" | "10" | "none"; label: string }[] = [
  { value: "3", label: "3" },
  { value: "5", label: "5" },
  { value: "10", label: "10" },
  { value: "none", label: "No limit" },
];

// Above this many themes the picker gets "Select all" + a search field.
const PICKER_SEARCH_THRESHOLD = 6;

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function runSummary(r: AutoDetectRunResult): string {
  const themesWithMatches = r.themes.filter((t) => t.added > 0).length;
  const head =
    r.added > 0
      ? `Added the top ${plural(r.added, "new excerpt")} across ${plural(themesWithMatches, "theme")}`
      : "No new excerpts found";
  const withCoded = r.alreadyCoded > 0 ? `${head} · ${r.alreadyCoded} already coded` : head;
  // Each run adds the next-best matches (already-coded ones are skipped), so say so.
  return r.added > 0 ? `${withCoded}. Run again to find more.` : withCoded;
}

interface ThemeConfigModalProps {
  projectId: string;
  onClose: () => void;
  /** Preset the themes to detect — hides the picker and shows "Theme: <name>". */
  themeIds?: string[];
  /** Search only these recordings (server intersects with what the user may see). */
  storyIds?: string[];
  /** Describes the `storyIds` scope, e.g. "In this recording" / "In 3 selected recordings". */
  scopeLabel?: string;
}

export default function ThemeConfigModal({
  projectId,
  onClose,
  themeIds: presetThemeIds,
  storyIds,
  scopeLabel,
}: ThemeConfigModalProps) {
  const dispatch = useQceDispatch();
  const { themes, loading: themesLoading } = useThemes(projectId);
  // The project's latest run (app-level): reopening mid-run shows it running.
  const runState = useSelector((s: RootState) => s.qce.autoDetectRuns[projectId]);
  const running = runState?.status === "running";
  const runResult = runState?.status === "done" ? runState.result ?? null : null;
  const runError = runState?.status === "error" ? runState.error ?? null : null;

  const [themeIds, setThemeIds] = useState<Set<string>>(
    () => new Set(runState?.status === "running" ? runState.themeIds : presetThemeIds)
  );
  const [themeSearch, setThemeSearch] = useState("");
  const [perTheme, setPerTheme] = useState<AutoDetectRunRequest["perTheme"]>(5);
  const [strength, setStrength] = useState<AutoDetectStrength>("balanced");
  const [perRecording, setPerRecording] = useState<"3" | "5" | "10" | "none">("5");

  // The modal can be taller than the viewport: keep the run status/result in view.
  const runStatusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (running || runResult || runError)
      runStatusRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [running, runResult, runError]);

  const toggle = (id: string) =>
    setThemeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // The holding "unclassified" theme isn't a detection target.
  const selectableThemes = themes.filter((t) => t.system !== "unclassified");
  const checkedThemeIds = selectableThemes.filter((t) => themeIds.has(t.id)).map((t) => t.id);
  const isPreset = Boolean(presetThemeIds?.length);
  const presetNames = selectableThemes.filter((t) => presetThemeIds?.includes(t.id)).map((t) => t.name);
  const hasPickerSearch = !isPreset && selectableThemes.length > PICKER_SEARCH_THRESHOLD;

  const visibleThemes = useMemo(() => {
    const q = themeSearch.trim().toLowerCase();
    return q ? selectableThemes.filter((t) => t.name.toLowerCase().includes(q)) : selectableThemes;
  }, [selectableThemes, themeSearch]);
  const allVisibleChecked = visibleThemes.length > 0 && visibleThemes.every((t) => themeIds.has(t.id));
  const toggleAllVisible = () =>
    setThemeIds((prev) => {
      const next = new Set(prev);
      visibleThemes.forEach((t) => (allVisibleChecked ? next.delete(t.id) : next.add(t.id)));
      return next;
    });

  // A run that finished while the dialog was closed was already announced by toast;
  // clear it so this entry point opens on a fresh form (a live run stays shown).
  useEffect(() => {
    if (runState && runState.status !== "running") dispatch(clearAutoDetectRun(projectId));
  }, []);

  // Fire and forget: the slice owns the request, status and completion toast.
  const run = () => {
    if (!checkedThemeIds.length || running) return;
    void dispatch(
      runThemeDetection({
        projectId,
        body: {
          themeIds: checkedThemeIds,
          ...(storyIds?.length ? { storyIds } : {}),
          perTheme,
          strength,
          perRecording: perRecording === "none" ? null : (Number(perRecording) as 3 | 5 | 10),
        },
      })
    );
  };

  // Closing never cancels a run; a finished run is forgotten so reopening starts fresh.
  const close = () => {
    if (!running) dispatch(clearAutoDetectRun(projectId));
    onClose();
  };

  const isPickerShown = !themesLoading && !isPreset && selectableThemes.length > 0;

  return (
    <Dialog
      open
      onClose={close}
      maxWidth="sm"
      fullWidth
      // Fixed size only when the theme list itself needs to scroll (many themes);
      // otherwise the dialog sizes to its content so a short list doesn't leave a gap.
      PaperProps={{
        sx: {
          borderRadius: "12px",
          ...(isPickerShown && hasPickerSearch && { height: "min(680px, calc(100vh - 64px))" }),
        },
      }}
    >
      <Box
        sx={{
          p: "24px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          boxSizing: "border-box",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="4px" flexShrink={0}>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#212427" }}>Auto-detection</Typography>
          <IconButton onClick={close} size="small" sx={{ "&:focus": { outline: "none" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: "14px", color: "#606971", mb: scopeLabel ? "8px" : "16px", flexShrink: 0 }}>
          Find excerpts across this project&apos;s recordings that match the selected themes and add them to those
          themes.
        </Typography>
        {scopeLabel && (
          <Typography
            sx={{
              alignSelf: "flex-start",
              fontSize: "13px",
              fontWeight: 600,
              color: "#1A7E70",
              backgroundColor: "#F0FAF7",
              borderRadius: "6px",
              px: "8px",
              py: "2px",
              mb: "16px",
              flexShrink: 0,
            }}
          >
            {scopeLabel}
          </Typography>
        )}

        {themesLoading ? (
          <Box display="flex" justifyContent="center" py="32px">
            <CircularProgress size={24} sx={{ color: "#1A7E70" }} />
          </Box>
        ) : selectableThemes.length === 0 ? (
          // With no real themes there is nothing to detect — info only.
          <>
            <Typography sx={{ fontSize: "14px", color: "#606971", mb: "20px" }}>
              Create at least one theme before running auto-detection.
            </Typography>
            <Box display="flex" justifyContent="flex-end">
              <TSSecondaryButton text="Close" onClick={close} />
            </Box>
          </>
        ) : (
          <>
            {isPreset ? (
              <Typography sx={{ fontSize: "14px", color: "#464C53", mb: "4px", flexShrink: 0 }}>
                Theme:{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#212427" }}>
                  {presetNames.join(", ")}
                </Box>
              </Typography>
            ) : (
              <>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb="8px" flexShrink={0}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#212427" }}>Themes to detect</Typography>
                  {hasPickerSearch && (
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={() => !running && toggleAllVisible()}
                      sx={{ cursor: running ? "default" : "pointer" }}
                    >
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#464C53" }}>Select all</Typography>
                      <Checkbox
                        size="small"
                        checked={allVisibleChecked}
                        indeterminate={!allVisibleChecked && visibleThemes.some((t) => themeIds.has(t.id))}
                        onChange={toggleAllVisible}
                        onClick={(e) => e.stopPropagation()}
                        disabled={running || visibleThemes.length === 0}
                      />
                    </Box>
                  )}
                </Box>
                {hasPickerSearch && (
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search themes…"
                    value={themeSearch}
                    onChange={(e) => setThemeSearch(e.target.value)}
                    sx={{ mb: "8px", flexShrink: 0 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                <Box
                  sx={{
                    flex: hasPickerSearch ? 1 : "0 1 auto",
                    minHeight: 0,
                    maxHeight: hasPickerSearch ? undefined : "280px",
                    overflow: "auto",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                >
                  {visibleThemes.map((t, i) => (
                    <Box
                      key={t.id}
                      onClick={() => !running && toggle(t.id)}
                      display="flex"
                      alignItems="center"
                      gap="12px"
                      sx={{
                        px: "12px",
                        py: "8px",
                        borderTop: i === 0 ? "none" : "1px solid #E5E7EB",
                        backgroundColor: "#fff",
                        cursor: running ? "default" : "pointer",
                        "&:hover": { backgroundColor: "#F7F9FC" },
                      }}
                    >
                      <ThemeIcon iconKey={t.icon} color={t.color} size={28} />
                      <Typography sx={{ flex: 1, fontSize: "14px", fontWeight: 600, color: "#212427" }}>
                        {t.name}
                      </Typography>
                      <Checkbox
                        checked={themeIds.has(t.id)}
                        onChange={() => toggle(t.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={running}
                      />
                    </Box>
                  ))}
                  {visibleThemes.length === 0 && (
                    <Typography sx={{ fontSize: "13px", color: "#9AA1A8", py: "12px", textAlign: "center" }}>
                      No themes match your search.
                    </Typography>
                  )}
                </Box>
              </>
            )}

            <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "8px", p: "16px", mt: "16px", flexShrink: 0 }}>
              <OptionRow label="Excerpts per theme">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={perTheme}
                  onChange={(_e, v) => v && setPerTheme(v)}
                  disabled={running}
                  sx={toggleGroupSx}
                >
                  {PER_THEME_OPTIONS.map((n) => (
                    <ToggleButton key={n} value={n}>
                      {n}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </OptionRow>

              <OptionRow label="Match strength">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={strength}
                  onChange={(_e, v) => v && setStrength(v)}
                  disabled={running}
                  sx={toggleGroupSx}
                >
                  {STRENGTH_OPTIONS.map((o) => (
                    <Tooltip key={o.value} title={o.tooltip}>
                      <ToggleButton value={o.value}>{o.label}</ToggleButton>
                    </Tooltip>
                  ))}
                </ToggleButtonGroup>
              </OptionRow>

              <OptionRow label="Max per recording">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={perRecording}
                  onChange={(_e, v) => v && setPerRecording(v)}
                  disabled={running}
                  sx={toggleGroupSx}
                >
                  {PER_RECORDING_OPTIONS.map((o) => (
                    <ToggleButton key={o.value} value={o.value}>
                      {o.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </OptionRow>
            </Box>

            {/* Run status — scrolled into view when it changes (see runStatusRef). */}
            <Box ref={runStatusRef} sx={{ flexShrink: 0, maxHeight: 180, overflow: "auto" }}>
              {runError && (
                <Alert
                  severity="error"
                  sx={{ mt: "12px" }}
                  action={
                    <Button
                      size="small"
                      color="inherit"
                      onClick={run}
                      disabled={running || !checkedThemeIds.length}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Retry
                    </Button>
                  }
                >
                  {runError}
                </Alert>
              )}

              {runResult && (
                <Box sx={{ mt: "12px", backgroundColor: "#F0FAF7", borderRadius: "8px", px: "12px", py: "10px" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A7E70" }}>
                    {runSummary(runResult)}
                  </Typography>
                  {runResult.recordingsMatched > 0 && (
                    <Typography sx={{ fontSize: "12px", color: "#606971", mb: "6px" }}>
                      Matched in {plural(runResult.recordingsMatched, "recording")}
                    </Typography>
                  )}
                  {runResult.themes.map((t) => (
                    <Box key={t.themeId} display="flex" justifyContent="space-between" gap="12px" sx={{ py: "2px" }}>
                      <Typography noWrap sx={{ fontSize: "13px", color: "#212427", minWidth: 0 }}>
                        {t.name}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: "#606971", flexShrink: 0 }}>
                        +{t.added}
                        {t.alreadyCoded > 0 ? ` · ${t.alreadyCoded} already coded` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Footer: primary action next to Close. */}
            <Box display="flex" alignItems="center" gap="12px" mt="20px" flexShrink={0}>
              <Typography sx={{ flex: 1, fontSize: "12px", color: running ? "#606971" : "#9AA1A8" }}>
                {running
                  ? "You can close this — we'll let you know when it's done."
                  : checkedThemeIds.length === 0
                  ? "Check at least one theme above."
                  : ""}
              </Typography>
              <TSSecondaryButton text="Close" onClick={close} />
              <TSPrimaryButton
                text={running ? "Running…" : "Run auto-detection"}
                onClick={run}
                loading={running}
                disabled={running || checkedThemeIds.length === 0}
                sx={{ fontSize: "15px" }}
              />
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}

const toggleGroupSx = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    px: "12px",
    py: "4px",
    color: "#464C53",
    "&.Mui-selected": { backgroundColor: "#F0FAF7", color: "#1A7E70" },
    "&:focus": { outline: "none" },
  },
} as const;

function OptionRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap="12px" mb="10px" flexWrap="wrap">
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#464C53" }}>{label}</Typography>
      {children}
    </Box>
  );
}
